import React, { useEffect } from "react";
import "./privacypolicy.scss";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";

const SECTIONS = [
  {
    title: "1. Introduction and who we are",
    paragraphs: [
      'This Privacy Policy (the "Policy") describes how Mezada Development and Software LTD, company registration number 515306199, with its registered office at Shvil Hameretz 4, Tel Aviv ("Mezada", "we", "us", or "our"), collects, uses, shares, and protects personal information in connection with the website available at mezada.net (the "Site"). We are the controller of personal information collected through the Site. This Policy should be read together with our Terms & Conditions. By using the Site, you acknowledge that you have read and understood this Policy.',
    ],
  },
  {
    title: "2. Relationship to the Joni product",
    paragraphs: [
      "Joni is a product of Mezada Development and Software LTD, available at joni.ai. This Policy applies to the Site only. Information collected in connection with the Joni product is governed exclusively by Joni’s own privacy policy, available at joni.ai.",
    ],
  },
  {
    title: "3. Information we collect",
    paragraphs: [
      <>
        <strong>Information you provide.</strong> When you submit the contact form on the Site, we collect the details you choose to share with us: your name, email address, phone number, and the content of your message.
      </>,
      <>
        <strong>Information collected automatically.</strong> When you visit the Site, we and our service providers automatically collect technical information such as your IP address, browser type and version, device type, operating system, referring pages, the pages you visit, and the dates and times of your visits. Some of this information is collected using cookies and similar technologies, as described in Section 6.
      </>,
      "We do not intentionally collect special categories of personal information through the Site, and we ask that you do not submit such information through the contact form.",
    ],
  },
  {
    title: "4. How we use information",
    paragraphs: [
      "We use personal information: (a) to respond to your inquiries and communicate with you; (b) to operate, maintain, secure, and improve the Site; (c) to analyze how the Site is used so that we can improve its content and performance; (d) to comply with legal obligations and enforce our Terms & Conditions; and (e) to protect the rights, safety, and property of Mezada, our users, or others. We do not sell personal information, and we do not use information submitted through the contact form for marketing purposes without your consent.",
    ],
  },
  {
    title: "5. Legal bases for processing",
    paragraphs: [
      "Where applicable law requires a legal basis for processing personal information, we rely on: your consent (for example, for non-essential cookies); our legitimate interests in operating, securing, and improving the Site and in responding to inquiries; and compliance with our legal obligations. Where we rely on your consent, you may withdraw it at any time, without affecting the lawfulness of processing carried out before the withdrawal.",
    ],
  },
  {
    title: "6. Cookies and similar technologies",
    paragraphs: [
      "The Site uses cookies, which are small text files stored on your device. We use: (a) essential cookies, which are required for the Site to function properly; and (b) analytics cookies, which help us understand how the Site is used (we use Google Analytics). When you first visit the Site, you can accept or decline non-essential cookies using the cookie banner. Declining non-essential cookies does not affect your ability to use the Site. You can also manage or delete cookies at any time through your browser settings. This section serves as the Site’s cookie policy.",
    ],
  },
  {
    title: "7. How we share information",
    paragraphs: [
      "We share personal information only as described in this Policy: with service providers that process information on our behalf, such as hosting, analytics, and email providers, under obligations to protect it; where required by law, regulation, legal process, or a request from a competent authority; in connection with a merger, acquisition, financing, or sale of assets, subject to appropriate protections; and where necessary to protect the rights, property, or safety of Mezada or others. We do not sell personal information.",
    ],
  },
  {
    title: "8. International transfers",
    paragraphs: [
      "The Site is hosted on servers located in Stockholm, Sweden, within the European Economic Area. Some of our service providers, including Google Analytics, may process information in other jurisdictions, including the United States. Where personal information is transferred internationally, we take steps designed to ensure that it receives an adequate level of protection consistent with applicable law, including through contractual safeguards and, where applicable, recognized transfer frameworks.",
    ],
  },
  {
    title: "9. Data retention",
    paragraphs: [
      "We retain personal information only for as long as necessary for the purposes described in this Policy, unless a longer retention period is required or permitted by law. As a general guideline, contact-form inquiries are retained for 24 months after the inquiry has been handled, and technical logs and analytics information are retained for up to 14 months.",
    ],
  },
  {
    title: "10. Security",
    paragraphs: [
      "We implement technical and organizational measures designed to protect personal information against unauthorized access, loss, misuse, or alteration, consistent with the requirements of applicable law, including the Israeli Protection of Privacy Regulations (Data Security), 5777-2017. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    title: "11. Your rights",
    paragraphs: [
      "Under the Israeli Protection of Privacy Law, 5741-1981, and subject to its conditions, you are entitled to review information about you held in a database and to request the correction or deletion of information that is inaccurate, incomplete, or not up to date. If you are located in the European Economic Area or the United Kingdom, you may have additional rights under applicable law, including rights of access, rectification, erasure, restriction of processing, data portability, and objection, as well as the right to withdraw consent and the right to lodge a complaint with a supervisory authority. To exercise any of these rights, please contact us at info@mezada.net. You may also contact the Israeli Privacy Protection Authority with privacy-related concerns.",
    ],
  },
  {
    title: "12. Children",
    paragraphs: [
      "The Site is a corporate website intended for a general professional audience and is not directed at children under the age of 16. We do not knowingly collect personal information from children. If you believe that a child has provided us with personal information, please contact us at info@mezada.net and we will delete it.",
    ],
  },
  {
    title: "13. Changes to this Policy",
    paragraphs: [
      'We may update this Policy from time to time. The updated version will be posted on this page with a revised "Last updated" date, and it will take effect upon posting. We encourage you to review this page periodically. Where required by law, we will provide additional notice of material changes.',
    ],
  },
  {
    title: "14. Contact",
    paragraphs: [
      "Questions, requests, or concerns regarding this Policy or your personal information can be sent to info@mezada.net.",
    ],
  },
];

const Privacypolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main className="mz-legal">
        <div className="mz-legal__header">
          <p className="mz-legal__kicker">LEGAL</p>
          <h1 className="mz-legal__title">Privacy Policy</h1>
          <p className="mz-legal__updated">LAST UPDATED · Aug 25, 2026</p>
        </div>
        <div className="mz-legal__body">
          {SECTIONS.map((section) => (
            <section key={section.title} className="mz-legal__section">
              <h2>{section.title}</h2>
              {section.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Privacypolicy;
